import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FaArrowLeft, FaHome, FaTimes, FaChevronLeft, FaChevronRight, FaPhone, FaHardHat, FaImages, FaMagic, FaSearchPlus, FaSearchMinus, FaUndo, FaExchangeAlt, FaEye, FaUsers } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CTASection from '../ui/CTASection';
import Footer from '../layout/Footer';
import BeforeAfterGallery from '../ui/BeforeAfterGallery';
import { generateGalleryImagesFromManifest, generateGalleryOnlyImages } from '../../utils/galleryUtils';
import FocusTrap from '../../utils/focusTrap';
import './Gallery.css';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(10);
  const [allGalleryImages, setAllGalleryImages] = useState([]);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [viewMode, setViewMode] = useState('gallery'); // 'gallery' or 'beforeAfter'
  const [beforeAfterPairs, setBeforeAfterPairs] = useState([]);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isPinching, setIsPinching] = useState(false);
  const [pinchStart, setPinchStart] = useState({ distance: 0, centerX: 0, centerY: 0 });
  const [lastZoomLevel, setLastZoomLevel] = useState(1);
  const [showZoomInfo, setShowZoomInfo] = useState(false);
  
  // Refs
  const modalRef = useRef(null);
  const imageContainerRef = useRef(null);
  const focusTrapRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const navigate = useNavigate();

  const handleContactClick = useCallback((e) => {
    e.preventDefault();
    window.location.href = 'tel:+13136316020';
  }, []);

  // Zoom functionality
  const zoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.25, 0.5);
      if (newZoom === 1) {
        setPanPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  // Wheel zoom for desktop
  const handleWheelZoom = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (e.deltaY < 0) {
        setZoomLevel(prev => Math.min(prev + 0.25, 3));
      } else {
        setZoomLevel(prev => {
          const newZoom = Math.max(prev - 0.25, 0.5);
          if (newZoom === 1) {
            setPanPosition({ x: 0, y: 0 });
          }
          return newZoom;
        });
      }
    }
  };

  // Touch handling for pinch zoom
  const getDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const constrainPanPosition = (newPanPosition) => {
    if (zoomLevel <= 1) return { x: 0, y: 0 };
    
    const container = imageContainerRef.current;
    if (!container) return newPanPosition;
    
    const containerRect = container.getBoundingClientRect();
    const image = container.querySelector('.modal-image');
    if (!image) return newPanPosition;
    
    const imageRect = image.getBoundingClientRect();
    const maxPanX = Math.max(0, (imageRect.width - containerRect.width) / 2);
    const maxPanY = Math.max(0, (imageRect.height - containerRect.height) / 2);
    
    return {
      x: Math.max(-maxPanX, Math.min(maxPanX, newPanPosition.x)),
      y: Math.max(-maxPanY, Math.min(maxPanY, newPanPosition.y))
    };
  };

  // Mouse drag for panning
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      const newPanPosition = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      };
      setPanPosition(constrainPanPosition(newPanPosition));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handling for pinch zoom and pan
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      setIsPinching(true);
      setIsDragging(false);
      const distance = getDistance(e.touches[0], e.touches[1]);
      setPinchStart({ distance, centerX: 0, centerY: 0 });
      setLastZoomLevel(zoomLevel);
      setShowZoomInfo(true);
      e.preventDefault();
    } else if (zoomLevel > 1 && e.touches.length === 1) {
      setIsDragging(true);
      setIsPinching(false);
      setDragStart({ 
        x: e.touches[0].clientX - panPosition.x, 
        y: e.touches[0].clientY - panPosition.y 
      });
      e.preventDefault();
    }
  };

  const handleTouchMove = (e) => {
    if (isPinching && e.touches.length === 2) {
      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / pinchStart.distance;
      const newZoom = Math.max(1, Math.min(3, lastZoomLevel * scale));
      setZoomLevel(newZoom);
      // Reset pan position when zoom reaches 100%
      if (newZoom === 1) {
        setPanPosition({ x: 0, y: 0 });
      }
      e.preventDefault();
    } else if (isDragging && zoomLevel > 1 && e.touches.length === 1) {
      const newPanPosition = {
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      };
      setPanPosition(constrainPanPosition(newPanPosition));
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e) => {
    if (isPinching && e.touches.length < 2) {
      setIsPinching(false);
      setLastZoomLevel(zoomLevel);
      // Hide zoom info immediately when pinch ends
      setShowZoomInfo(false);
    }
    if (isDragging && e.touches.length === 0) {
      setIsDragging(false);
    }
  };

  // Function to create before/after pairs
  const createBeforeAfterPairs = (images) => {
    const pairs = [];
    // Extract filename from src path
    const beforeImages = images.filter(img => {
      const filename = img.src.split('/').pop();
      return filename && filename.startsWith('before-');
    });
    const afterImages = images.filter(img => {
      const filename = img.src.split('/').pop();
      return filename && filename.startsWith('after-');
    });
    
    
    beforeImages.forEach(beforeImg => {
      const filename = beforeImg.src.split('/').pop();
      const beforeNumber = filename.match(/before-(\d+)\.webp/)?.[1];
      if (beforeNumber) {
        const afterImg = afterImages.find(after => {
          const afterFilename = after.src.split('/').pop();
          return afterFilename === `after-${beforeNumber}.webp`;
        });
        if (afterImg) {
          pairs.push({
            id: `pair-${beforeNumber}`,
            before: {
              src: beforeImg.src,
              alt: `Before renovation - Project ${beforeNumber}`
            },
            after: {
              src: afterImg.src,
              alt: `After renovation - Project ${beforeNumber}`
            },
            projectNumber: beforeNumber
          });
        }
      }
    });
    
    
    return pairs.sort((a, b) => parseInt(a.projectNumber) - parseInt(b.projectNumber));
  };

  // Load images from manifest
  useEffect(() => {
    const loadImages = async () => {
      try {
        // Load all images for before/after pairs
        const allImages = await generateGalleryImagesFromManifest('/images/portfolio/');
        
        // Load only gallery images for gallery view
        const galleryOnlyImages = await generateGalleryOnlyImages('/images/portfolio/');
        setAllGalleryImages(galleryOnlyImages);
        
        // Create before/after pairs from all images
        const pairs = createBeforeAfterPairs(allImages);
        setBeforeAfterPairs(pairs);
      } catch (error) {
        console.error('Error loading gallery images:', error);
        setAllGalleryImages([]);
        setBeforeAfterPairs([]);
      }
    };

    loadImages();
  }, []);

  const visibleImages = useMemo(() => 
    allGalleryImages.slice(0, visibleCount), 
    [allGalleryImages, visibleCount]
  );

  const hasMoreImages = useMemo(() => 
    visibleCount < allGalleryImages.length, 
    [visibleCount, allGalleryImages.length]
  );

  const openModal = (image, index) => {
    // Store scroll position before opening modal
    scrollPositionRef.current = window.scrollY;
    setSelectedImage(image);
    setCurrentIndex(index);
    setIsModalOpen(true);
    resetZoom();
  };

  const closeModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsModalOpen(false);
    setSelectedImage(null);
    resetZoom();
  };

  const nextImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const nextIndex = (currentIndex + 1) % allGalleryImages.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(allGalleryImages[nextIndex]);
    resetZoom();
  };

  const prevImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const prevIndex = (currentIndex - 1 + allGalleryImages.length) % allGalleryImages.length;
    setCurrentIndex(prevIndex);
    setSelectedImage(allGalleryImages[prevIndex]);
    resetZoom();
  };

  // Function to announce text to screen readers
  const announceToScreenReader = (message) => {
    // Try to use the modal's live region first, then fallback to body region
    let liveRegion = document.getElementById('modal-image-announcement');
    if (!liveRegion) {
      liveRegion = document.getElementById('image-announcement');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'image-announcement';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.style.position = 'absolute';
        liveRegion.style.left = '-10000px';
        liveRegion.style.width = '1px';
        liveRegion.style.height = '1px';
        liveRegion.style.overflow = 'hidden';
        liveRegion.style.clip = 'rect(0,0,0,0)';
        document.body.appendChild(liveRegion);
      }
    }
    
    // Clear previous content first
    liveRegion.textContent = '';
    
    // Set the new message with a small delay to ensure screen reader picks it up
    setTimeout(() => {
      liveRegion.textContent = message;
    }, 50);
  };


  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isModalOpen) return;
    
    switch (e.key) {
      case 'Escape':
        closeModal();
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        nextImage();
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        prevImage();
        break;
      case '+':
      case '=':
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          setZoomLevel(prev => Math.min(prev + 0.25, 3));
        }
        break;
      case '-':
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          setZoomLevel(prev => {
            const newZoom = Math.max(prev - 0.25, 0.5);
            if (newZoom === 1) {
              setPanPosition({ x: 0, y: 0 });
            }
            return newZoom;
          });
        }
        break;
      case '0':
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          setZoomLevel(1);
          setPanPosition({ x: 0, y: 0 });
        }
        break;
      default:
        break;
    }
  };

  const handleShowMore = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Store the previous visible count
    const previousVisibleCount = visibleCount;
    
    setVisibleCount(prev => Math.min(prev + 10, allGalleryImages.length));
    
    // Focus on the first newly added gallery item after DOM update
    setTimeout(() => {
      // Find all gallery items
      const allGalleryItems = document.querySelectorAll('.gallery-item');
      
      // Get the first newly added item (at index previousVisibleCount)
      const newlyAddedItem = allGalleryItems[previousVisibleCount];
      
      if (newlyAddedItem) {
        // Find the focusable element (button or link)
        const focusableElement = newlyAddedItem.querySelector('button, a, [href]');
        if (focusableElement) {
          focusableElement.focus({ preventScroll: true });
          // Scroll the element into view smoothly
          newlyAddedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
          // If no focusable element, make the item itself focusable
          newlyAddedItem.setAttribute('tabindex', '-1');
          newlyAddedItem.focus({ preventScroll: true });
          newlyAddedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }, 100);
  };


  // Modal effects
  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      
      // Prevent body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      // Initialize focus trap
      if (modalRef.current) {
        focusTrapRef.current = new FocusTrap(modalRef.current);
        focusTrapRef.current.activate();
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        
        // Store the scroll position to restore
        const scrollY = scrollPositionRef.current;
        
        // Temporarily disable smooth scrolling on html element
        const htmlElement = document.documentElement;
        const originalScrollBehavior = htmlElement.style.scrollBehavior;
        htmlElement.style.scrollBehavior = 'auto';
        
        // Remove fixed positioning from body
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Use requestAnimationFrame to ensure styles are removed before scrolling
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
          
          // Restore smooth scrolling after scroll is complete
          requestAnimationFrame(() => {
            htmlElement.style.scrollBehavior = originalScrollBehavior;
          });
        });
        
        // Deactivate focus trap
        if (focusTrapRef.current) {
          focusTrapRef.current.deactivate();
          focusTrapRef.current = null;
        }
      };
    }
  }, [isModalOpen]);

  // Reset zoom when changing images
  useEffect(() => {
    if (selectedImage) {
      resetZoom();
    }
  }, [selectedImage]);

  // Global mouse/touch event listeners for panning and pinch zoom
  useEffect(() => {
    if (isDragging || isPinching) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, isPinching, dragStart, panPosition, pinchStart, lastZoomLevel]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (focusTrapRef.current) {
        focusTrapRef.current.deactivate();
        focusTrapRef.current = null;
      }
    };
  }, []);

  return (
    <main className="gallery-page">
      <Helmet>
        <title>RC Remodel Portfolio - Before & After Home Renovation Gallery | 400+ Projects</title>
        <meta name="description" content="Browse our extensive portfolio of 400+ completed home renovation projects. View before & after transformations of kitchens, bathrooms, basements, and full home remodels. Professional results by licensed contractors." />
        <meta name="keywords" content="home remodeling portfolio, before and after renovation, kitchen remodel gallery, bathroom renovation photos, home transformation gallery, remodeling project photos, renovation before after, home improvement gallery" />
        <link rel="canonical" href="https://rcremodel.com/gallery" />
        
        {/* Open Graph */}
        <meta property="og:title" content="RC Remodel Portfolio - Before & After Home Renovation Gallery" />
        <meta property="og:description" content="Browse our extensive portfolio of 400+ completed home renovation projects. View before & after transformations of kitchens, bathrooms, and full home remodels." />
        <meta property="og:url" content="https://rcremodel.com/gallery" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://rcremodel.com/images/portfolio/gallery/renovation-2.webp" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="RC Remodel Portfolio - Before & After Gallery" />
        <meta name="twitter:description" content="Browse 400+ completed home renovation projects. View before & after transformations by licensed contractors." />
        <meta name="twitter:image" content="https://rcremodel.com/images/portfolio/gallery/renovation-2.webp" />
      </Helmet>
      
      <div className="page-header">
        <div className="container">
          <div className="page-nav">
            <Link to="/" className="back-btn">
              <FaArrowLeft className="back-icon" />
              Back to Home
            </Link>
            <div className="page-nav-right">
              <button className="nav-action-btn" onClick={handleContactClick} title="Call us">
                <FaPhone className="nav-icon" />
                <span className="nav-text">Call us</span>
              </button>
              <Link to="/all-services" className="nav-action-btn" title="Our Services">
                <FaHardHat className="nav-icon" />
                <span className="nav-text">Our Services</span>
              </Link>
              <Link to="/gc-services" className="nav-action-btn" title="General Contractor">
                <FaUsers className="nav-icon" />
                <span className="nav-text">General Contractor</span>
              </Link>
              <button className="nav-action-btn active" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} title="Our Gallery">
                <FaImages className="nav-icon" />
                <span className="nav-text">Our Gallery</span>
              </button>
            </div>
          </div>
          <h1 className="page-title">Our Gallery</h1>
          <p className="page-subtitle">
            Highlighting our renovation and remodeling projects.
          </p>
        </div>
      </div>

      <div className="gallery-content">
        <div className="container">
          <div className="gallery-intro">
            <h2 className="gallery-heading">See how we turn ideas into results</h2>
            
            {/* View Mode Toggle */}
            <div className="view-mode-toggle">
              <button 
                type="button"
                className={`view-mode-btn ${viewMode === 'gallery' ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setViewMode('gallery');
                }}
                aria-label="Switch to gallery view"
              >
                <FaImages />
                <span>Gallery View</span>
              </button>
              <button 
                type="button"
                className={`view-mode-btn ${viewMode === 'beforeAfter' ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setViewMode('beforeAfter');
                }}
                aria-label="Switch to before and after comparison view"
              >
                <FaExchangeAlt />
                <span>Before & After</span>
              </button>
            </div>
          </div>
            {viewMode === 'gallery' ? (
              <div className="gallery-collage">
                {visibleImages.map((image, index) => (
                  <div 
                    key={image.id} 
                    className={`gallery-item ${image.size}`}
                    onClick={() => openModal(image, index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openModal(image, index);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`View ${image.alt} in full size`}
                  >
                    <div className="gallery-image">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="gallery-img"
                        loading="lazy"
                        decoding="async"
                        onLoad={() => setLoadedImages(prev => new Set([...prev, image.id]))}
                        onError={() => setLoadedImages(prev => new Set([...prev, image.id]))}
                        style={{
                          opacity: loadedImages.has(image.id) ? 1 : 0,
                          transition: 'opacity 0.3s ease',
                          willChange: 'opacity'
                        }}
                      />
                      <div className="gallery-overlay">
                        <FaMagic className="view-icon" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <BeforeAfterGallery 
                beforeAfterPairs={beforeAfterPairs}
              />
            )}

          {/* Show More Button - Only for gallery view */}
          {viewMode === 'gallery' && hasMoreImages && (
            <div className="show-more-section">
              <button 
                type="button" 
                className="show-more-btn" 
                onClick={handleShowMore}
              >
                Show More Images
                <span className="show-more-count">
                  ({allGalleryImages.length - visibleCount} remaining)
                </span>
              </button>
            </div>
          )}

          {/* Call to Action */}
          <CTASection />
        </div>
      </div>

      {/* Image Modal */}
      {isModalOpen && selectedImage && (
        <div 
          className="image-modal-overlay" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            closeModal(e);
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div 
            ref={modalRef}
            className="image-modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              type="button"
              className="modal-close-btn"
              onClick={closeModal}
              aria-label="Close image modal"
            >
              <FaTimes />
            </button>

            {/* Navigation Buttons */}
            {zoomLevel === 1 && (
              <>
                <button 
                  type="button"
                  className="modal-nav-btn modal-prev-btn"
                  onClick={prevImage}
                  aria-label="Previous image"
                >
                  <FaChevronLeft />
                </button>
                <button 
                  type="button"
                  className="modal-nav-btn modal-next-btn"
                  onClick={nextImage}
                  aria-label="Next image"
                >
                  <FaChevronRight />
                </button>
              </>
            )}

            {/* Image Container */}
            <div 
              ref={imageContainerRef}
              className={`modal-image-container ${isDragging ? 'grabbing' : zoomLevel > 1 ? 'grabbable' : ''}`}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onWheel={handleWheelZoom}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="modal-image"
                style={{
                  transform: `scale(${zoomLevel}) translate(${panPosition.x}px, ${panPosition.y}px)`,
                  transition: isDragging || isPinching ? 'none' : 'transform 0.3s ease-out'
                }}
                loading="eager"
                decoding="sync"
              />
            </div>

            {/* Mobile/Tablet Zoom Info */}
            {showZoomInfo && (
              <div className="mobile-zoom-indicator">
                <div className="mobile-zoom-level">
                  {Math.round(zoomLevel * 100)}%
                </div>
                <div className="mobile-zoom-hint">
                  Pinch to zoom • Pan to move
                </div>
              </div>
            )}

            {/* Zoom Controls - Desktop Only */}
            <div className="modal-zoom-controls desktop-only">
              <button 
                type="button"
                className="zoom-control-btn zoom-in-control"
                onClick={zoomIn}
                aria-label={`Zoom in image. Current zoom level: ${Math.round(zoomLevel * 100)}%. Press to increase zoom.`}
                title="Zoom in (Ctrl/Cmd + Plus)"
                disabled={zoomLevel >= 3}
              >
                <FaSearchPlus />
              </button>
              {zoomLevel > 1 && (
                <button 
                  type="button"
                  className="zoom-control-btn zoom-out-control"
                  onClick={zoomOut}
                  aria-label={`Zoom out image. Current zoom level: ${Math.round(zoomLevel * 100)}%. Press to decrease zoom.`}
                  title="Zoom out (Ctrl/Cmd + Minus)"
                  disabled={zoomLevel <= 0.5}
                >
                  <FaSearchMinus />
                </button>
              )}
              {zoomLevel !== 1 && (
                <button 
                  type="button"
                  className="zoom-control-btn zoom-reset-control"
                  onClick={resetZoom}
                  aria-label={`Reset zoom to 100%. Current zoom level: ${Math.round(zoomLevel * 100)}%.`}
                  title="Reset zoom (Ctrl/Cmd + 0)"
                >
                  <FaUndo />
                </button>
              )}
              {zoomLevel > 1 && (
                <div className="zoom-level-display">
                  {Math.round(zoomLevel * 100)}%
                </div>
              )}
            </div>

            {/* Image Info */}
            {zoomLevel === 1 && (
              <div className="modal-info">
                <div className="modal-counter">
                  {currentIndex + 1} of {allGalleryImages.length}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      <Footer />
    </main>
  );
};

export default GalleryPage;
